import { createContext, useContext, useEffect, useState } from "react";
import { createSupabaseClient } from "./supabase";
import type { AuthUser } from "./types";
import type { User } from "@supabase/supabase-js";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({
  children,
  initialUser,
}: {
  children: React.ReactNode;
  initialUser?: any; // Allow serialized data from server
}) {
  const [user, setUser] = useState<AuthUser | null>(initialUser || null);
  const [loading, setLoading] = useState(!initialUser);

  useEffect(() => {
    // Only run in browser
    if (typeof window === "undefined") return;

    const supabase = createSupabaseClient();

    // Get initial session
    const getInitialSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        // Fetch user profile from your API
        const response = await fetch("/api/auth/me", {
          credentials: "include",
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      }
      setLoading(false);
    };

    // Always check session, but only if we don't have a user already
    if (!user) {
      getInitialSession();
    } else {
      setLoading(false);
    }

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: any, session: any) => {
      if (event === "SIGNED_IN" && session?.user) {
        // Fetch user profile
        const response = await fetch("/api/auth/me", {
          credentials: "include",
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } else if (event === "SIGNED_OUT") {
        setUser(null);
      } else if (event === "TOKEN_REFRESHED" && session?.user) {
        // Also fetch profile on token refresh
        const response = await fetch("/api/auth/me", {
          credentials: "include",
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [initialUser]);

  const signOut = async () => {
    if (typeof window !== "undefined") {
      const supabase = createSupabaseClient();
      await supabase.auth.signOut();
    }
    setUser(null);
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// 인증 상태 확인 유틸리티
export const useAuthGuard = () => {
  const { isAuthenticated, loading } = useAuth();

  const requireAuth = (redirectTo?: string) => {
    if (!loading && !isAuthenticated) {
      const currentPath =
        typeof window !== "undefined" ? window.location.pathname : "/";
      const loginUrl = `/auth-required?redirect=${encodeURIComponent(
        redirectTo || currentPath
      )}`;
      if (typeof window !== "undefined") {
        window.location.href = loginUrl;
      }
      return false;
    }
    return true;
  };

  return { requireAuth, isAuthenticated, loading };
};
